import React, { useState, useEffect, useMemo } from 'react';

import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import TabNavigation from '../../components/ui/TabNavigation';
import ServiceTable from './components/ServiceTable';
import ServiceFormModal from './components/ServiceFormModal';
import Button from '../../components/ui/Button';

const ServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch services from Supabase with retry logic for schema cache
  const fetchServices = async (retryCount = 0) => {
    try {
      const { data, error } = await supabase?.from('services')?.select('*')?.order('created_at', { ascending: false });

      if (error) {
        // Handle schema cache error with retry
        if (error?.code === 'PGRST205' && retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          console.log(`Schema cache not ready, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchServices(retryCount + 1);
        }
        throw error;
      }
      setServices(data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching services:', err);
      if (err?.code === 'PGRST205') {
        setError('Database schema is updating. Please refresh the page in a moment.');
      } else {
        setError('Failed to load services');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const filteredServices = React.useMemo(() => {
    return services?.filter(service => {
      const matchesSearch = service?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           service?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesFilter = filterStatus === 'all' ||
                           (filterStatus === 'active' && service?.is_available) ||
                           (filterStatus === 'inactive' && !service?.is_available);
      return matchesSearch && matchesFilter;
    });
  }, [services, searchTerm, filterStatus]);

  const handleAddService = () => {
    setEditingService(null);
    setIsModalOpen(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setIsModalOpen(true);
  };

  const handleDuplicateService = async (service) => {
    try {
      const duplicatedService = {
        name: `${service?.name} (Copy)`,
        description: service?.description,
        duration: service?.duration,
        price: service?.price,
        is_available: service?.is_available,
        available_days: service?.available_days,
        time_ranges: service?.time_ranges,
        buffer_time: service?.buffer_time,
        max_daily_bookings: service?.max_daily_bookings,
        booking_count: 0
      };

      const { data, error } = await supabase?.from('services')?.insert([duplicatedService])?.select()?.single();

      if (error) throw error;
      setServices(prev => [data, ...prev]);
    } catch (err) {
      console.error('Error duplicating service:', err);
      alert('Failed to duplicate service. Please try again.');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      try {
        const { error } = await supabase?.from('services')?.delete()?.eq('id', serviceId);

        if (error) throw error;

        setServices(prev => prev?.filter(s => s?.id !== serviceId));
        setSelectedServices(prev => prev?.filter(id => id !== serviceId));
      } catch (err) {
        console.error('Error deleting service:', err);
        alert('Failed to delete service. Please try again.');
      }
    }
  };

  const handleSaveService = async (serviceData) => {
    try {
      // Convert camelCase to snake_case for database
      const dbData = {
        name: serviceData?.name,
        description: serviceData?.description || '',
        duration: 30, // Default duration since form doesn't collect it
        price: 0, // Default price since form doesn't collect it
        is_available: true,
        available_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        time_ranges: [{ start: '09:00', end: '17:00' }],
        buffer_time: 10,
        max_daily_bookings: 10
      };

      if (editingService) {
        // Update existing service
        const { data, error } = await supabase?.from('services')?.update(dbData)?.eq('id', editingService?.id)?.select()?.single();

        if (error) throw error;
        setServices(prev => prev?.map(s => s?.id === editingService?.id ? data : s));
      } else {
        // Create new service
        const { data, error } = await supabase?.from('services')?.insert([dbData])?.select()?.single();

        if (error) throw error;
        setServices(prev => [data, ...prev]);
      }

      setIsModalOpen(false);
      setEditingService(null);
    } catch (err) {
      console.error('Error saving service:', err);
      throw new Error('Failed to save service. Please try again.');
    }
  };

  const handleSelectService = (serviceId, checked) => {
    if (checked) {
      setSelectedServices(prev => [...prev, serviceId]);
    } else {
      setSelectedServices(prev => prev?.filter(id => id !== serviceId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedServices(filteredServices?.map(s => s?.id));
    } else {
      setSelectedServices([]);
    }
  };

  const handleToggleStatus = async (serviceId) => {
    try {
      const service = services?.find(s => s?.id === serviceId);
      if (!service) return;

      const { data, error } = await supabase
        ?.from('services')
        ?.update({ is_available: !service?.is_available })
        ?.eq('id', serviceId)
        ?.select()
        ?.single();

      if (error) throw error;
      setServices(prev => prev?.map(s => s?.id === serviceId ? data : s));
    } catch (err) {
      console.error('Error toggling service status:', err);
      alert('Failed to toggle service status. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading services...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <TabNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error text-lg mb-2">Error Loading Data</p>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => fetchServices()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stats = {
    total: services?.length || 0,
    active: services?.filter(s => s?.is_available)?.length || 0,
    inactive: services?.filter(s => !s?.is_available)?.length || 0,
    totalBookings: services?.reduce((sum, s) => sum + (s?.booking_count || 0), 0) || 0
  };

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <div className="container mx-auto px-4 py-6 md:py-8 lg:py-10">
        <div className="flex flex-col gap-6 md:gap-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Service Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Define and manage your service offerings, pricing, and availability
              </p>
            </div>
            <Button
              variant="default"
              size="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddService}
              className="w-full md:w-auto"
            >
              Add Service
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Services</span>
                <Icon name="Briefcase" size={20} className="text-primary" />
              </div>
              <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stats?.total}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Active Services</span>
                <Icon name="CheckCircle" size={20} className="text-success" />
              </div>
              <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stats?.active}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Inactive Services</span>
                <Icon name="XCircle" size={20} className="text-muted-foreground" />
              </div>
              <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stats?.inactive}</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Bookings</span>
                <Icon name="Calendar" size={20} className="text-primary" />
              </div>
              <p className="text-2xl md:text-3xl font-heading font-bold text-foreground">{stats?.totalBookings}</p>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border shadow-elevation-1">
            <div className="p-4 md:p-6 border-b border-border">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search services..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e?.target?.value)}
                    className="px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth"
                  >
                    <option value="all">All Services</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                  </select>
                </div>
              </div>
            </div>

            <ServiceTable
              services={filteredServices}
              onEdit={handleEditService}
              onDuplicate={handleDuplicateService}
              onDelete={handleDeleteService}
              onToggleStatus={handleToggleStatus}
              selectedServices={selectedServices}
              onSelectService={handleSelectService}
              onSelectAll={handleSelectAll}
            />
          </div>
        </div>
      </div>

      <ServiceFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveService}
        service={editingService}
      />
    </div>
  );
};

export default ServiceManagement;